import { View } from "react-native";
import { Input } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useDispatch } from "react-redux";
import {
  removeFiltersPayload,
  setFiltersPayload,
  useValueFromPayloadRange,
} from "@redux/slices/searchFilters";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useTranslation } from "react-i18next";
import useResetFilter from "../../../../hooks/useResetFilter";
import { VDBIcons } from "@utils/VDBIcons";
import {
  commaFormattedValue,
  handleBlurMinMax,
  removeCommaFormatted,
} from "../FilterSection/Utils";
import { useUserCurrency } from "../../../../hooks/getCurrency";
import { SearchFilterNameEnum, SearchFilterTypeEnum } from "../utils";
import { formatDiscountValue } from "@components/molecules/DynamicForm/utils";
import { removePercentage } from "@utils/product/productFormatters";
import { useAppSettings } from "@redux/slices/globalSlice";

type RangeSectionProps = {
  data: any;
  sectionName?: string;
  selectedStyle: SelectedItemTheme;
  minLabel?: string;
  maxLabel?: string;
  subSectionName?: string;
  direction?: string;
  step?: number;
  web_width?: number;
  parentWidth?: number;
};

const RangeSection = ({
  data = {},
  sectionName = "",
  selectedStyle,
  minLabel = "",
  maxLabel = "",
  subSectionName = "",
  direction = "column",
  step = 0,
  parentWidth,
}: RangeSectionProps) => {
  const isParcelCarat =
    data?.name === "Carat" && sectionName === SearchFilterTypeEnum.PARCEL;
  const value = useValueFromPayloadRange(sectionName, data?.payload_name);
  const isTouched = useRef(false);
  const { t } = useTranslation();
  const userCurrency = useUserCurrency();
  const userInfo = useUserInfo();
  const getAppSettings = useAppSettings();
  const enableFourDecimalCarat =
    getAppSettings?.enable_four_decimal_carat ?? false;

  const theme = useTheme();
  const dispatch = useDispatch();
  const basePayloadKey = Array.isArray(data?.payload_name)
    ? data?.payload_name?.[0]
    : data?.payload_name;
  const baseSectionKey =
    basePayloadKey || subSectionName || sectionName || "range";
  const safeKey = (mValue: unknown) =>
    String(mValue ?? "item")
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9._-]/g, "");
  const baseTestId = `filters.${
    sectionName?.toLowerCase()?.replaceAll(" ", "-")?.toString() || "section"
  }.${safeKey(baseSectionKey)}.range`;
  const web_width = data.web_width;
  const useWeb_width =
    web_width && step === 1 && (theme.isLargeScreen || theme.isTablet);
  const isTotalPrice = subSectionName === SearchFilterNameEnum.TOTAL_PRICE;
  const isPricePerCarat =
    subSectionName === SearchFilterNameEnum.PRICE_PER_CARAT;
  const showCurrencySymbol = isTotalPrice || isPricePerCarat;
  const mMinLabel = useMemo(() => {
    let label = t(minLabel || "", { defaultValue: "Min" });

    if (
      userCurrency?.length > 0 &&
      showCurrencySymbol &&
      !label.includes(userCurrency)
    ) {
      label = label.replace("$", userCurrency);
    }

    return label;
  }, [minLabel, userCurrency, showCurrencySymbol, userInfo.user.locale]);

  const mMaxLabel = useMemo(() => {
    let label = t(maxLabel || "", { defaultValue: "Max" });

    if (
      userCurrency?.length > 0 &&
      showCurrencySymbol &&
      !label.includes(userCurrency)
    ) {
      label = label.replace("$", userCurrency);
    }

    return label;
  }, [maxLabel, userCurrency, showCurrencySymbol, userInfo.user.locale]);

  const getPixelWidth = () => {
    if (useWeb_width && parentWidth) {
      const totalWidth = parentWidth + 2 * SpaceType.Extra;

      return (web_width / 100) * totalWidth;
    }
    return "100%";
  };

  const [minAndMax, setMinAndMax] = useState({
    min:
      value?.[0] !== undefined
        ? commaFormattedValue(
            subSectionName === "Discount" && value[0]
              ? `${value[0]}%`
              : value[0],
            isTotalPrice || isPricePerCarat
          )
        : null,
    max:
      value?.[1] !== undefined
        ? commaFormattedValue(
            subSectionName === "Discount" && value[1]
              ? `${value[1]}%`
              : value[1],
            isTotalPrice || isPricePerCarat
          )
        : null,
  });

  useResetFilter(() => {
    setMinAndMax({
      min: null,
      max: null,
    });
  });

  const handleMinMaxChange = (text: string, isMin: boolean) => {
    isTouched.current = true;
    let formattedValue: string;
    if (subSectionName !== "Discount") {
      const lastChar = text.slice(-1);
      const isDecimalAttempt = lastChar === "." || lastChar === ",";
      const hasExistingDecimal = text.split(".").length > 2;

      let workingText = text.replace(/,/g, "");

      if (hasExistingDecimal) {
        const parts = workingText.split(".");
        workingText = `${parts[0]}.${parts[1]}`;
      }

      workingText = workingText.replace(/[^0-9.]/g, "");

      if (workingText === "") {
        setMinAndMax((old) => ({
          ...old,
          [isMin ? "min" : "max"]: null,
        }));
        return;
      }

      const [wholePart, decimalPart] = workingText.split(".");

      let formattedWhole = "";
      if (wholePart) {
        formattedWhole =
          commaFormattedValue(wholePart, isTotalPrice || isPricePerCarat) ?? "";
      }

      if (isDecimalAttempt || decimalPart !== undefined) {
        formattedValue = `${formattedWhole}.${decimalPart || ""}`;
      } else {
        formattedValue = formattedWhole;
      }
    } else {
      formattedValue = formatDiscountValue(text, true);
    }

    setMinAndMax((old) => ({
      ...old,
      [isMin ? "min" : "max"]: formattedValue,
    }));
  };

  useEffect(() => {
    if (!isTouched.current) return;

    const payload: any = {};
    if (
      data?.payload_name &&
      minAndMax.min === null &&
      minAndMax.max === null &&
      sectionName
    ) {
      const payloadName = data?.payload_name;
      dispatch(removeFiltersPayload({ sectionName, payloadName }));
    } else if (data?.payload_name) {
      if (minAndMax.min !== null) {
        if (subSectionName) {
          const mMinValue = Number(removeCommaFormatted(minAndMax.min ?? ""));
          if (mMinValue > 0) {
            payload[data?.payload_name[0]] = mMinValue.toString();
          } else if (subSectionName === "Discount") {
            if (minAndMax.max === null) {
              payload[data?.payload_name[1]] = "";
            }
            payload[data?.payload_name[0]] = removePercentage(
              minAndMax.min?.toString() ?? ""
            );
          } else {
            payload[data?.payload_name[0]] = "";
          }
        }
      } else payload[data?.payload_name[0]] = "";
      if (minAndMax.max !== null) {
        if (subSectionName) {
          const mMaxValue = Number(removeCommaFormatted(minAndMax.max ?? ""));
          if (mMaxValue > 0 && subSectionName !== "Discount") {
            payload[data?.payload_name[1]] = mMaxValue.toString();
          } else if (subSectionName === "Discount") {
            if (minAndMax.min === null) {
              payload[data?.payload_name[0]] = "";
            }
            payload[data?.payload_name[1]] = removePercentage(
              minAndMax.max?.toString() ?? ""
            );
          } else {
            payload[data?.payload_name[1]] = "";
          }
        }
      } else payload[data?.payload_name[1]] = "";

      if (sectionName) {
        if (
          payload[data?.payload_name[0]] === "" &&
          payload[data?.payload_name[1]] === ""
        ) {
          const payloadName = data?.payload_name;
          dispatch(removeFiltersPayload({ sectionName, payloadName }));
        } else if (
          payload[data?.payload_name[0]] === "" &&
          subSectionName !== "Discount"
        ) {
          if (payload[data?.payload_name[1]] !== "") {
            let maxPayload: any = {};
            maxPayload[data?.payload_name[1]] = payload[data?.payload_name[1]];
            dispatch(setFiltersPayload({ sectionName, data: maxPayload }));
          }
          const payloadName = [];
          payloadName.push(data?.payload_name[0]);
          dispatch(removeFiltersPayload({ sectionName, payloadName }));
        } else if (
          payload[data?.payload_name[1]] === "" &&
          subSectionName !== "Discount"
        ) {
          if (payload[data?.payload_name[0]] !== "") {
            let minPayload: any = {};
            minPayload[data?.payload_name[0]] = payload[data?.payload_name[0]];

            dispatch(setFiltersPayload({ sectionName, data: minPayload }));
          }
          const payloadName = [];
          payloadName.push(data?.payload_name[1]);
          dispatch(removeFiltersPayload({ sectionName, payloadName }));
        } else {
          dispatch(setFiltersPayload({ sectionName, data: payload }));
        }
      }
    }
  }, [minAndMax]);

  useEffect(() => {
    if (!isParcelCarat) return;
    if (!Array.isArray(value)) return;

    setMinAndMax((prev) => ({
      ...prev,
      min:
        value[0] !== undefined &&
        value[0] !== null &&
        Number.isFinite(Number(value[0]))
          ? Number(value[0]).toFixed(enableFourDecimalCarat ? 4 : 3)
          : null,
      max:
        value[1] !== undefined &&
        value[1] !== null &&
        Number.isFinite(Number(value[1]))
          ? Number(value[1]).toFixed(enableFourDecimalCarat ? 4 : 3)
          : null,
    }));
  }, []);

  return (
    <ParentContainer
      direction={direction}
      web_width={web_width}
      useWeb_width={useWeb_width}
      pixelWidth={getPixelWidth()}
      hasParentWidth={!!parentWidth}
    >
      <CompareView>
        <View style={{ flex: 1 }}>
          <Input
            inputLabel={mMinLabel}
            value={minAndMax.min ? minAndMax.min?.toString() : ""}
            onChangeText={(text) => handleMinMaxChange(text, true)}
            onBlur={() =>
              handleBlurMinMax(
                true,
                minAndMax,
                setMinAndMax,
                isTotalPrice || isPricePerCarat,
                subSectionName,
                dispatch,
                sectionName,
                data?.payload_name,
                isParcelCarat,
                enableFourDecimalCarat
              )
            }
            {...(subSectionName === "Discount" && {
              onFocus: () => {
                if (!minAndMax.min) {
                  setMinAndMax((prev) => ({ ...prev, min: "-" }));
                }
              },
            })}
            {...(subSectionName === "Discount"
              ? {
                  keyboardType: "numbers-and-punctuation",
                }
              : { keyboardType: "decimal-pad" })}
            selectedStyle={selectedStyle}
            showCurrency={!!minAndMax.min && showCurrencySymbol}
            testID={`${baseTestId}.minInput`}
            data-testId={`${baseTestId}.minInput`}
          />
        </View>
        <VDBIcon
          name={VDBIcons.ARROW_RIGHT}
          size={24}
          color={theme["core-04"]}
          variant={IconsVariant.VDBCustom}
        />
        <View style={{ flex: 1 }}>
          <Input
            inputLabel={mMaxLabel}
            value={minAndMax.max ? minAndMax.max?.toString() : ""}
            onChangeText={(text) => handleMinMaxChange(text, false)}
            onBlur={() =>
              handleBlurMinMax(
                false,
                minAndMax,
                setMinAndMax,
                isTotalPrice || isPricePerCarat,
                subSectionName,
                dispatch,
                sectionName,
                data?.payload_name,
                isParcelCarat,
                enableFourDecimalCarat
              )
            }
            {...(subSectionName === "Discount" && {
              onFocus: () => {
                if (!minAndMax.max) {
                  setMinAndMax((prev) => ({ ...prev, max: "-" }));
                }
              },
            })}
            {...(subSectionName === "Discount"
              ? {
                  keyboardType: "numbers-and-punctuation",
                }
              : { keyboardType: "decimal-pad" })}
            selectedStyle={selectedStyle}
            showCurrency={!!minAndMax.max && showCurrencySymbol}
            testID={`${baseTestId}.maxInput`}
            data-testId={`${baseTestId}.maxInput`}
          />
        </View>
      </CompareView>
    </ParentContainer>
  );
};

const CompareView = styled.View`
  gap: ${SpaceType.Basic}px;
  flex-direction: row;
  align-items: center;
`;

const ParentContainer = styled.View<{
  direction: string;
  web_width?: number;
  useWeb_width?: boolean;
  pixelWidth: number | string;
  hasParentWidth: boolean;
}>`
  ${({ direction }: { direction?: string }) =>
    direction === "row" ? "flex: 1;" : ""}
  ${({
    pixelWidth,
    hasParentWidth,
  }: {
    pixelWidth: number | string;
    hasParentWidth: boolean;
  }) => (hasParentWidth ? `width: ${pixelWidth}px;` : "width: 100%;")}
  padding-left: ${SpaceType.Extra}px;
  padding-right: ${SpaceType.Extra}px;
`;

export default memo(RangeSection);
